import { useEffect } from 'react';

export type Falsy = false | 0 | '' | null | undefined;

/**
 * Tiny class-name joiner. Filters out falsy values so callers can write
 * cn('base', cond && 'extra', className) without ternaries everywhere.
 *
 * Intentionally NOT tailwind-merge: our primitives accept a single
 * `className` escape hatch that is always appended last, so callers'
 * overrides win by virtue of source order. Saves ~6KB gzipped vs.
 * pulling in tailwind-merge.
 */
export const cn = (...xs: Array<string | Falsy>): string =>
  xs.filter(Boolean).join(' ');

/**
 * Lock body scroll while an overlay (sheet, modal) is open. Restores
 * the previous overflow value on cleanup. Uses `overflow: hidden` on
 * the body (not `position: fixed`) because the fixed-position trick
 * scrolls the page to the top on iOS Safari when the overlay closes.
 */
export const useBodyScrollLock = (locked: boolean): void => {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
};

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Trap Tab/Shift+Tab focus inside `containerRef` while `active` is
 * true. On activation, focus moves to the first focusable element
 * inside the container (unless a child is already focused). On
 * deactivation, focus is restored to whatever was active before.
 */
export const useFocusTrap = (
  containerRef: React.RefObject<HTMLElement>,
  active: boolean,
): void => {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    const initial = focusables()[0];
    if (initial && !container.contains(document.activeElement)) {
      initial.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [active, containerRef]);
};
